import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { db } from '../../services/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useNotifications } from '../../contexts/NotificationContext';

const AdminAppointments = () => {
  const { createAppointmentNotification } = useNotifications();
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState('ALL');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [metrics, setMetrics] = useState({
    total: 0,
    today: 0,
    pending: 0,
    approved: 0,
    completed: 0,
    rejected: 0
  });

  useEffect(() => {
    loadAppointments();
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadAppointments = async () => {
    let appointments = [];
    
    console.log('Admin appointments page loading data from Firebase...');
    
    try {
      const appointmentsRef = collection(db, 'appointments');
      const querySnapshot = await getDocs(appointmentsRef);
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        appointments.push({
          id: doc.id,
          ...data,
          // Convert Firestore Timestamp to Date if needed
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        });
      });
      
      console.log('Admin appointments from Firebase:', appointments.length);
      console.log('Appointment details:', appointments);
    } catch (firebaseError) {
      console.error('Admin Firebase appointments load failed:', firebaseError.message);
      appointments = [];
    }
    
    // Sort by creation date (newest first)
    appointments.sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    console.log('Admin final appointments:', appointments);
    setAppointments(appointments);
    calculateMetrics(appointments);
  };

  const calculateMetrics = (appointmentsList) => {
    const today = new Date().toISOString().split('T')[0];
    
    const metrics = {
      total: appointmentsList.length,
      today: appointmentsList.filter(apt => apt.date === today).length,
      pending: appointmentsList.filter(apt => apt.status === 'pending').length,
      approved: appointmentsList.filter(apt => apt.status === 'confirmed').length,
      completed: appointmentsList.filter(apt => apt.status === 'completed').length,
      rejected: appointmentsList.filter(apt => apt.status === 'cancelled').length
    };
    
    setMetrics(metrics);
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      const updatedAppointments = appointments.map(apt => 
        apt.id === appointmentId 
          ? { ...apt, status: newStatus, updatedAt: new Date() }
          : apt
      );
      
      setAppointments(updatedAppointments);
      calculateMetrics(updatedAppointments);

      // Send notification to client
      const appointment = appointments.find(apt => apt.id === appointmentId);
      if (appointment && appointment.userId) {
        try {
          let notificationType = '';
          let successMessage = '';
          
          if (newStatus === 'confirmed') {
            notificationType = 'appointment_confirmed';
            successMessage = `✅ Appointment confirmed! Client has been notified.`;
            setAlertType('success');
          } else if (newStatus === 'cancelled' || newStatus === 'rejected') {
            notificationType = 'appointment_cancelled';
            successMessage = `❌ Appointment rejected. Client has been notified.`;
            setAlertType('error');
          } else if (newStatus === 'completed') {
            notificationType = 'appointment_completed';
            successMessage = `✓ Appointment marked as completed. Client has been notified.`;
            setAlertType('success');
          }
          
          if (notificationType) {
            await createAppointmentNotification(
              { ...appointment, status: newStatus }, 
              notificationType, 
              appointment.userId
            );
            console.log(`Notification sent to client for ${notificationType}`);
            
            // Show success alert
            setAlertMessage(successMessage);
            setShowSuccessAlert(true);
            setTimeout(() => {
              setShowSuccessAlert(false);
            }, 4000);
          }
        } catch (notificationError) {
          console.error('Error sending notification to client:', notificationError);
          setAlertMessage('⚠️ Status updated but failed to notify client.');
          setAlertType('warning');
          setShowSuccessAlert(true);
          setTimeout(() => {
            setShowSuccessAlert(false);
          }, 4000);
        }
      }

      console.log(`Appointment ${appointmentId} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating appointment status:', error);
      setAlertMessage('❌ Failed to update appointment status. Please try again.');
      setAlertType('error');
      setShowSuccessAlert(true);
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 4000);
    }
  };

  const viewAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedAppointment(null);
  };

  const getFilteredAppointments = () => {
    switch (activeTab) {
      case 'TODAY':
        const today = new Date().toISOString().split('T')[0];
        return appointments.filter(apt => apt.date === today);
      case 'PENDING':
        return appointments.filter(apt => apt.status === 'pending');
      case 'APPROVED':
        return appointments.filter(apt => apt.status === 'confirmed');
      case 'COMPLETED':
        return appointments.filter(apt => apt.status === 'completed');
      case 'REJECTED':
        return appointments.filter(apt => apt.status === 'cancelled');
      default:
        return appointments;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'confirmed': return '#4caf50';
      case 'completed': return '#2196f3';
      case 'cancelled': return '#f44336';
      default: return '#666';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'confirmed': return 'Approved';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Rejected';
      default: return status;
    }
  };

  const deleteAppointment = async (appointmentId) => {
    try {
      const confirmDelete = window.confirm('Are you sure you want to delete this appointment? This action cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'appointments', appointmentId));
      const updated = appointments.filter(a => a.id !== appointmentId);
      setAppointments(updated);
      calculateMetrics(updated);
      setAlertMessage('Appointment deleted successfully.');
      setAlertType('success');
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setAlertMessage('Failed to delete appointment.');
      setAlertType('error');
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 3000);
    }
  };

  const filteredAppointments = getFilteredAppointments();

  return (
    <Layout>
      {/* Success/Error Alert */}
      {showSuccessAlert && (
        <div style={{
          position: 'fixed',
          top: '80px',
          right: '20px',
          zIndex: 9999,
          background: alertType === 'success' ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' :
                      alertType === 'error' ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' :
                      'linear-gradient(135deg, #ffa751 0%, #ffe259 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          minWidth: '300px',
          maxWidth: '500px',
          animation: 'slideInRight 0.3s ease-out',
          fontSize: '0.95rem',
          fontWeight: '500'
        }}>
          <div style={{ fontSize: '1.5rem' }}>
            {alertType === 'success' ? '✓' : alertType === 'error' ? '✕' : '⚠'}
          </div>
          <div style={{ flex: 1 }}>{alertMessage}</div>
          <button
            onClick={() => setShowSuccessAlert(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>
      )}

      <div className="analytics-container admin-appointments-page" style={{ 
        padding: windowWidth < 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box',
        height: 'auto',
        minHeight: 'auto'
      }}>
        {/* Header */}
        <div style={{ 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            gap: windowWidth < 768 ? '1rem' : '0',
            maxWidth: '100%'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
                fontWeight: 'bold', 
                color: '#333', 
                margin: 0,
                wordBreak: 'break-word'
              }}>
                Manage Appointments
              </h1>
              <p style={{ 
                color: '#666', 
                marginTop: '0.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '1rem',
                margin: '0.5rem 0 0 0'
              }}>
                View and manage all customer appointments
              </p>
            </div>
            
            <button 
              className="btn btn-primary"
              onClick={loadAppointments}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: windowWidth < 768 ? '0.75rem 1rem' : '0.75rem 1.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '0.875rem',
                whiteSpace: 'nowrap',
                width: windowWidth < 768 ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <img 
                src="/refresh.jpg" 
                alt="Refresh" 
                style={{ 
                  width: windowWidth < 768 ? '18px' : '24px', 
                  height: windowWidth < 768 ? '18px' : '24px', 
                  objectFit: 'contain'
                }} 
              />
              Refresh
            </button>
          </div>
        </div>

        {/* Metrics Cards - HARDCODED ALL 4 CARDS */}
        {console.log('Rendering appointments metrics - windowWidth:', windowWidth, 'metrics:', metrics)}
        <div className="stats-cards-container" style={{ 
          display: windowWidth <= 480 ? 'flex' : 'grid',
          flexDirection: windowWidth <= 480 ? 'column' : 'row',
          gridTemplateColumns: windowWidth <= 480 ? '1fr' : 
                               windowWidth <= 768 ? '1fr 1fr' : 
                               windowWidth <= 1024 ? 'repeat(2, 1fr)' :
                               'repeat(4, 1fr)', 
          gap: '0.75rem',
          marginBottom: '2rem',
          padding: '1rem',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'visible',
          boxSizing: 'border-box',
          minHeight: windowWidth <= 480 ? '400px' : 'auto',
          height: 'auto'
        }}>
          {/* CARD 1: Total Appointments */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Total Appointments
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.total || 0}
            </div>
          </div>

          {/* CARD 2: Pending Approval */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Pending Approval
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.pending || 0}
            </div>
          </div>

          {/* CARD 3: Approved */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Approved
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.approved || 0}
            </div>
          </div>

          {/* CARD 4: Completed */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Completed
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.completed || 0}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid #eee', 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          maxWidth: '100%'
        }}>
          {[
            { key: 'ALL', label: `ALL (${metrics.total})`, shortLabel: `ALL (${metrics.total})` },
            { key: 'TODAY', label: `TODAY (${metrics.today})`, shortLabel: `TODAY (${metrics.today})` },
            { key: 'PENDING', label: `PENDING (${metrics.pending})`, shortLabel: `PEND (${metrics.pending})` },
            { key: 'APPROVED', label: `APPROVED (${metrics.approved})`, shortLabel: `APPR (${metrics.approved})` },
            { key: 'COMPLETED', label: `COMPLETED (${metrics.completed})`, shortLabel: `COMP (${metrics.completed})` },
            { key: 'REJECTED', label: `REJECTED (${metrics.rejected})`, shortLabel: `REJ (${metrics.rejected})` }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none',
                border: 'none',
                padding: windowWidth < 768 ? '0.75rem 1rem' : '1rem 1.5rem',
                fontSize: windowWidth < 480 ? '0.75rem' : '0.875rem',
                fontWeight: '600',
                color: activeTab === tab.key ? '#2196f3' : '#666',
                borderBottom: activeTab === tab.key ? '2px solid #2196f3' : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: windowWidth < 480 ? 'auto' : '120px'
              }}
            >
              {windowWidth <= 480 ? tab.shortLabel : tab.label}
            </button>
          ))}
        </div>

        {/* Appointments Table */}
        <div className="card" style={{ 
          padding: 0, 
          overflow: 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {filteredAppointments.length > 0 ? (
            <>
              {windowWidth >= 768 ? (
                // Desktop Table Layout
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Customer</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Service</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Date & Time</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Status</th>
                        <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAppointments.map((appointment, index) => (
                        <tr key={appointment.id} style={{ 
                          borderBottom: '1px solid #eee',
                          background: index % 2 === 0 ? 'white' : '#fafafa'
                        }}>
                          <td style={{ padding: '1rem' }}>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                {appointment.userName || 'Unknown User'}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                {appointment.userEmail}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ fontSize: '0.875rem' }}>{appointment.service}</div>
                            {appointment.notes && (
                              <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                                {appointment.notes}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                              {new Date(appointment.date).toLocaleDateString()}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                              {appointment.time}
                            </div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span className={`status-badge-modern ${
                              appointment.status === 'confirmed' ? 'status-success-modern' :
                              appointment.status === 'pending' ? 'status-warning-modern' :
                              appointment.status === 'completed' ? 'status-info-modern' :
                              appointment.status === 'cancelled' || appointment.status === 'rejected' ? 'status-error-modern' :
                              'status-pending-modern'
                            }`}>
                              {getStatusLabel(appointment.status)}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                              <button 
                                onClick={() => viewAppointmentDetails(appointment)}
                                style={{
                                  background: 'none',
                                  border: '1px solid #ddd',
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.875rem'
                                }}
                                title="View Details"
                              >
                                🔍
                              </button>
                              <button 
                                onClick={() => deleteAppointment(appointment.id)}
                                style={{
                                  background: 'none',
                                  color: 'inherit',
                                  border: '1px solid #ddd',
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.875rem'
                                }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                              
                              {appointment.status === 'pending' && (
                                <>
                                  <button 
                                    onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                                    style={{
                                      background: 'none',
                                      color: 'inherit',
                                      border: '1px solid #ddd',
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.875rem'
                                    }}
                                    title="Approve"
                                  >
                                    ✅
                                  </button>
                                  <button 
                                    onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                                    style={{
                                      background: 'none',
                                      color: 'inherit',
                                      border: '1px solid #ddd',
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.875rem'
                                    }}
                                    title="Reject"
                                  >
                                    ❌
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Mobile Card Layout
                <div style={{ 
                  padding: windowWidth < 480 ? '0.75rem' : '1rem',
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '1rem'
                }}>
                  {filteredAppointments.map((appointment, index) => (
                    <div
                      key={appointment.id}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: windowWidth < 480 ? '1rem' : '1.25rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                      }}
                    >
                      {/* Mobile Card Header */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem',
                        gap: '1rem'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: windowWidth < 480 ? '0.9rem' : '1rem',
                            marginBottom: '0.25rem',
                            wordBreak: 'break-word'
                          }}>
                            {appointment.userName || 'Unknown User'}
                          </div>
                          <div style={{ 
                            fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem', 
                            color: '#666',
                            wordBreak: 'break-word'
                          }}>
                            {appointment.userEmail}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <span className={`status-badge-modern ${
                            appointment.status === 'confirmed' ? 'status-success-modern' :
                            appointment.status === 'pending' ? 'status-warning-modern' :
                            appointment.status === 'completed' ? 'status-info-modern' :
                            appointment.status === 'cancelled' || appointment.status === 'rejected' ? 'status-error-modern' :
                            'status-pending-modern'
                          }`} style={{
                            fontSize: windowWidth < 480 ? '0.7rem' : '0.75rem',
                            padding: windowWidth < 480 ? '0.25rem 0.5rem' : '0.375rem 0.75rem'
                          }}>
                            {getStatusLabel(appointment.status)}
                          </span>
                        </div>
                      </div>

                      {/* Mobile Card Content */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '0.75rem',
                        fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem'
                      }}>
                        <div>
                          <div style={{ 
                            fontWeight: '500', 
                            marginBottom: '0.25rem',
                            fontSize: windowWidth < 480 ? '0.85rem' : '0.9rem'
                          }}>
                            🔧 {appointment.service}
                          </div>
                          {appointment.notes && (
                            <div style={{ 
                              color: '#666',
                              fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem',
                              fontStyle: 'italic'
                            }}>
                              "{appointment.notes}"
                            </div>
                          )}
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '0.75rem',
                          borderTop: '1px solid #e5e7eb',
                          fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem',
                          color: '#666'
                        }}>
                          <div>
                            <div style={{ fontWeight: '600', color: '#333', marginBottom: '0.25rem' }}>
                              📅 {new Date(appointment.date).toLocaleDateString()}
                            </div>
                            <div>
                              ⏰ {appointment.time}
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button 
                              onClick={() => viewAppointmentDetails(appointment)}
                              style={{
                                background: 'none',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                padding: windowWidth < 480 ? '0.5rem' : '0.625rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem'
                              }}
                              title="View Details"
                            >
                              🔍
                            </button>
                            <button 
                              onClick={() => deleteAppointment(appointment.id)}
                              style={{
                                background: 'none',
                                color: 'inherit',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                padding: windowWidth < 480 ? '0.5rem' : '0.625rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem'
                              }}
                              title="Delete"
                            >
                              🗑️
                            </button>
                            
                            {appointment.status === 'pending' && (
                              <>
                                <button 
                                  onClick={() => updateAppointmentStatus(appointment.id, 'confirmed')}
                                  style={{
                                    background: 'none',
                                    color: 'inherit',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: windowWidth < 480 ? '0.5rem' : '0.625rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem'
                                  }}
                                  title="Approve"
                                >
                                  ✓
                                </button>
                                <button 
                                  onClick={() => updateAppointmentStatus(appointment.id, 'cancelled')}
                                  style={{
                                    background: 'none',
                                    color: 'inherit',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: windowWidth < 480 ? '0.5rem' : '0.625rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem'
                                  }}
                                  title="Reject"
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⊡</div>
              <h3 style={{ marginBottom: '0.5rem' }}>No Appointments Found</h3>
              <p>
                {activeTab === 'ALL' 
                  ? 'Appointments will appear here when customers book services.'
                  : `No ${activeTab.toLowerCase()} appointments found.`
                }
              </p>
            </div>
          )}
        </div>

        {/* Appointment Details Modal */}
        {showDetailsModal && selectedAppointment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: windowWidth < 768 ? '1rem 0.5rem' : '2rem'
          }} onClick={closeDetailsModal}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: windowWidth < 768 ? '8px' : '12px',
              padding: windowWidth < 480 ? '1.5rem' : '2rem',
              maxWidth: windowWidth < 768 ? '100%' : '600px',
              width: windowWidth < 768 ? '100%' : '90%',
              maxHeight: windowWidth < 768 ? 'calc(100vh - 2rem)' : '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              margin: windowWidth < 768 ? '0' : 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '1.5rem',
                borderBottom: '2px solid #eee',
                paddingBottom: '1rem'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  color: '#2e7d32', 
                  fontSize: windowWidth < 480 ? '1.25rem' : '1.5rem',
                  wordBreak: 'break-word',
                  flex: 1,
                  marginRight: '1rem'
                }}>
                  Appointment Details
                </h2>
                <button 
                  onClick={closeDetailsModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: windowWidth < 768 ? '1.25rem' : '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem',
                    flexShrink: 0
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: windowWidth < 768 ? '1fr' : '1fr 1fr', 
                  gap: '1rem' 
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Customer Information</h3>
                    <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                      <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600' }}>
                        {selectedAppointment.userName || 'Unknown User'}
                      </p>
                      <p style={{ margin: '0', color: '#666', fontSize: '0.875rem' }}>
                        {selectedAppointment.userEmail}
                      </p>
                      {selectedAppointment.phone && (
                        <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.875rem' }}>
                          📞 {selectedAppointment.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Appointment Status</h3>
                    <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                      <span style={{
                        background: getStatusColor(selectedAppointment.status),
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.875rem',
                        fontWeight: '600'
                      }}>
                        {getStatusLabel(selectedAppointment.status)}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Service Details</h3>
                  <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', fontSize: '1.1rem' }}>
                      {selectedAppointment.service}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem' }}>
                      <div>
                        <p style={{ margin: '0', color: '#666', fontSize: '0.875rem' }}>⊡ Date</p>
                        <p style={{ margin: '0', fontWeight: '600' }}>
                          {new Date(selectedAppointment.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: '0', color: '#666', fontSize: '0.875rem' }}>⏰ Time</p>
                        <p style={{ margin: '0', fontWeight: '600' }}>
                          {selectedAppointment.time}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedAppointment.address && (
                  <div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Service Address</h3>
                    <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                      <p style={{ margin: '0', fontSize: '0.875rem' }}>
                        📍 {selectedAppointment.address}
                      </p>
                    </div>
                  </div>
                )}

                {selectedAppointment.notes && (
                  <div>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Additional Notes</h3>
                    <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                      <p style={{ margin: '0', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {selectedAppointment.notes}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1rem' }}>Timestamps</h3>
                  <div style={{ background: '#f9f9f9', padding: '1rem', borderRadius: '6px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
                      <div>
                        <p style={{ margin: '0', color: '#666' }}>Created</p>
                        <p style={{ margin: '0' }}>
                          {selectedAppointment.createdAt ? 
                            new Date(selectedAppointment.createdAt).toLocaleString() : 
                            'N/A'
                          }
                        </p>
                      </div>
                      <div>
                        <p style={{ margin: '0', color: '#666' }}>Last Updated</p>
                        <p style={{ margin: '0' }}>
                          {selectedAppointment.updatedAt ? 
                            new Date(selectedAppointment.updatedAt).toLocaleString() : 
                            'N/A'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedAppointment.status === 'pending' && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: windowWidth < 480 ? 'column' : 'row',
                    gap: windowWidth < 480 ? '0.75rem' : '1rem', 
                    marginTop: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #eee'
                  }}>
                    <button 
                      onClick={() => {
                        updateAppointmentStatus(selectedAppointment.id, 'confirmed');
                        closeDetailsModal();
                      }}
                      style={{
                        background: 'none',
                        color: 'inherit',
                        border: '1px solid #ddd',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                        fontWeight: '600',
                        flex: 1,
                        width: windowWidth < 480 ? '100%' : 'auto'
                      }}
                    >
                      ✓ Approve Appointment
                    </button>
                    <button 
                      onClick={() => {
                        updateAppointmentStatus(selectedAppointment.id, 'cancelled');
                        closeDetailsModal();
                      }}
                      style={{
                        background: 'none',
                        color: 'inherit',
                        border: '1px solid #ddd',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                        fontWeight: '600',
                        flex: 1,
                        width: windowWidth < 480 ? '100%' : 'auto'
                      }}
                    >
                      ✕ Reject Appointment
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for alert animation */}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AdminAppointments;